import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShoppingBag, MessageCircle, User, Award, Users } from "lucide-react";
import { useCart } from "../../context/CartContext";
import { useChat } from "../../context/ChatContext";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { getTierNames } from "../../utils/tierNames";

const DEFAULT_LOGO_URL =
  "https://customer-assets.emergentagent.com/wingman/7b8b7474-e52e-4388-adc0-a2361520c0a4/attachments/8840390a845949e6b9092405676c1743_image.png";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { setOpen: openCart, itemCount } = useCart();
  const { setOpen: openChat } = useChat();
  const { user } = useAuth();
  const { settings } = useTheme();

  const tierNames = getTierNames(settings);
  const NAV = [
    { id: "start", label: tierNames.start.name },
    { id: "power", label: tierNames.power.name },
    { id: "elite", label: tierNames.elite.name },
    { id: "brand", label: "Brand" },
  ];

  const logoUrl = settings?.logo_url || DEFAULT_LOGO_URL;
  const logoWidth = Number(settings?.logo_width) || 180;
  const alignMap = { left: "justify-start", center: "justify-center", right: "justify-end" };
  const logoJustify = alignMap[settings?.logo_alignment] || "justify-start";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="leovera-header"
      style={{
        backgroundColor: scrolled
          ? "color-mix(in srgb, var(--lv-bg,#060A14) 85%, transparent)"
          : "color-mix(in srgb, var(--lv-bg,#060A14) 40%, transparent)",
      }}
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl border-b border-white/10"
          : "backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
        <div className={`flex ${logoJustify} items-center flex-1 lg:flex-none min-w-0`}>
          <button onClick={() => scrollTo("hero")} className="flex items-center group"
                  data-testid="logo-btn" aria-label="LEOVERA Nutrition">
            <img src={logoUrl} alt="LEOVERA Nutrition"
                 data-testid="header-logo"
                 style={{ width: `${logoWidth}px` }}
                 className="h-auto max-h-16 object-contain rounded-md bg-white p-1.5 shadow-[0_0_0_1px_rgba(192,192,192,0.25)] transition-transform duration-300 group-hover:scale-[1.02]" />
          </button>
        </div>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`nav-${n.id}`}
                    className="text-[12px] tracking-[0.28em] uppercase text-[#94A3B8] hover:text-white transition-colors">
              {n.label}
            </button>
          ))}
          <Link to="/forum" data-testid="nav-forum"
                className="text-[12px] tracking-[0.28em] uppercase text-[#94A3B8] hover:text-white transition-colors inline-flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Forum
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-2.5">
          {user ? (
            <Link to="/account" data-testid="header-account-btn"
                  className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-[#C0C0C0]/40 text-[#E5E4E2] hover:bg-[#C0C0C0]/10 transition-all text-[10px] tracking-[0.24em] uppercase rounded-sm">
              <Award className="w-3.5 h-3.5 text-amber-300" />
              <span className="hidden xl:inline">{user.name?.split(" ")[0] || "Account"} ·</span>
              <span data-testid="header-loyalty-points">{user.loyalty_points || 0} pt</span>
            </Link>
          ) : (
            <Link to="/login" data-testid="header-login-btn"
                  className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-white/15 text-[#E5E4E2] hover:border-[#C0C0C0] transition-all text-[10px] tracking-[0.24em] uppercase rounded-sm">
              <User className="w-3.5 h-3.5" />
              Accedi
            </Link>
          )}
          <button onClick={() => openChat(true)} data-testid="header-chat-btn"
                  className="inline-flex items-center gap-2 px-3.5 py-2.5 border border-emerald-400/40 text-[#E5E4E2] hover:bg-emerald-400 hover:text-[#060A14] transition-all text-[10px] tracking-[0.24em] uppercase rounded-sm">
            <MessageCircle className="w-3.5 h-3.5" />
            Leo
          </button>
          <button onClick={() => openCart(true)} data-testid="header-cart-btn" aria-label="Apri carrello"
                  className="relative inline-flex items-center gap-2 px-4 py-2.5 bg-[#C0C0C0] text-[#060A14] hover:bg-white transition-all text-[10px] tracking-[0.24em] uppercase font-medium rounded-sm">
            <ShoppingBag className="w-3.5 h-3.5" />
            Carrello
            {itemCount > 0 && (
              <span data-testid="header-cart-badge"
                    className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                {itemCount}
              </span>
            )}
          </button>
        </div>

        <button className="md:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 backdrop-blur-xl" style={{ backgroundColor: "color-mix(in srgb, var(--lv-bg,#060A14) 95%, transparent)" }}>
          <div className="px-6 py-6 flex flex-col gap-3">
            {NAV.map((n) => (
              <button key={n.id} onClick={() => scrollTo(n.id)} data-testid={`mobile-nav-${n.id}`}
                      className="text-left text-[13px] tracking-[0.28em] uppercase text-[#E5E4E2] py-2">
                {n.label}
              </button>
            ))}
            <Link to="/forum" onClick={() => setOpen(false)} data-testid="mobile-nav-forum"
                  className="text-left text-[13px] tracking-[0.28em] uppercase text-[#E5E4E2] py-2 inline-flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-300" /> Forum
            </Link>
            {user ? (
              <Link to="/account" onClick={() => setOpen(false)} data-testid="mobile-account-btn"
                    className="inline-flex justify-center items-center gap-2 px-5 py-3 border border-[#C0C0C0]/40 text-[#E5E4E2] text-[11px] tracking-[0.28em] uppercase rounded-sm">
                <Award className="w-4 h-4 text-amber-300" />
                Account · {user.loyalty_points || 0} pt
              </Link>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-login-btn"
                    className="inline-flex justify-center items-center gap-2 px-5 py-3 border border-white/15 text-[#E5E4E2] text-[11px] tracking-[0.28em] uppercase rounded-sm">
                <User className="w-4 h-4" /> Accedi / Registrati
              </Link>
            )}
            <button onClick={() => { openChat(true); setOpen(false); }} data-testid="mobile-chat-btn"
                    className="inline-flex justify-center items-center gap-2 px-5 py-3 border border-emerald-400/40 text-[#E5E4E2] text-[11px] tracking-[0.28em] uppercase rounded-sm">
              <MessageCircle className="w-4 h-4 text-emerald-400" />
              Parla con Leo
            </button>
            <button onClick={() => { openCart(true); setOpen(false); }} data-testid="mobile-cart-btn"
                    className="inline-flex justify-center items-center gap-2 px-5 py-3 bg-[#C0C0C0] text-[#060A14] text-[11px] tracking-[0.28em] uppercase font-medium rounded-sm">
              <ShoppingBag className="w-4 h-4" />
              Carrello {itemCount > 0 && <span className="ml-1">({itemCount})</span>}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
